import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import i18n from '@/locales/i18n.ts'
import { useLoginStore } from '@/store/LoginStore.ts'

/** 서버·네트워크 오류로 갱신하지 못했을 때 로딩 화면을 유지한 채 다시 시도하는 간격 */
const INIT_RETRY_DELAYS_MS = [1000, 3000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * 앱 초기 로딩 시 HttpOnly 쿠키(Refresh Token)를 이용해
 * Access Token을 복구(Silent Refresh)하는 훅
 */
export const useAuthInit = (options: { enabled?: boolean } = { enabled: true }) => {
    const [isInitialized, setIsInitialized] = useState(false)
    const login = useLoginStore((state) => state.login)
    const accessToken = useLoginStore((state) => state.accessToken)

    useEffect(() => {
        let cancelled = false

        const initializeAuth = async () => {
            // 초기화가 비활성화된 경우(예: 공용 페이지) 바로 완료 처리
            if (options.enabled === false) {
                setIsInitialized(true)
                return
            }

            // 이미 메모리에 토큰이 있다면 초기화 완료
            if (accessToken) {
                setIsInitialized(true)
                return
            }

            try {
                const { apiRefreshOutcome } = await import('@/api/auth/refresh.ts')
                let outcome = await apiRefreshOutcome()

                // 서버·네트워크 오류는 잠시 뒤 다시 시도한다. 429 는 기다려도 풀리지 않으므로 바로 넘어간다.
                for (const delay of INIT_RETRY_DELAYS_MS) {
                    if (outcome.status !== 'unavailable' || outcome.reason === 'rate_limited') break
                    await sleep(delay)
                    if (cancelled) return
                    outcome = await apiRefreshOutcome({ bypassCooldown: true })
                }
                if (cancelled) return

                if (outcome.status === 'ok') {
                    await login('', outcome.token)
                } else if (outcome.status === 'unavailable' && outcome.reason === 'rate_limited') {
                    toast.info(i18n.t('auth.session.restoreRateLimited'))
                }
            } finally {
                if (!cancelled) setIsInitialized(true)
            }
        }

        initializeAuth()

        return () => {
            cancelled = true
        }
    }, [accessToken, login, options.enabled])

    return isInitialized
}
